/**
 * Client della Gestione Tornei (/api/gare/*, /api/public/gare/*).
 *
 * Due modalità, decise dall'URL:
 * - Control Center (/gare/...): Basic Auth gestita dal browser, come il resto del CC.
 * - App staff (/staff/...): token personale (nome + PIN) salvato sul telefono.
 */

export type GaraStatus = "setup" | "live" | "finished";
export type Phase = "group" | "semi" | "final" | "third" | "gold_semi" | "gold_final" | "gold_third" | "round";
export type GaraFormat = "gironi_tabellone" | "coppie_fisse" | "giro_a_8";

export type GaraConfig = {
  groups?: number;
  per_group?: number;
  courts: string[];
  group_minutes: number;
  third_place?: boolean;
  gold?: boolean;
  diamond_courts?: string[];
  gold_courts?: string[];
};

export type GaraSummary = {
  id: number;
  playtomic_tid: string | null;
  name: string;
  category: string;
  start_at: string | null;
  format: GaraFormat;
  format_label: string;
  status: GaraStatus;
  road_to: number;
  n_players: number;
  n_teams: number;
  n_matches: number;
  n_done: number;
};

export type Player = {
  id: number;
  name: string;
  level: number | null;
  gender: string | null;
  picture: string | null;
  role: "player" | "fake" | "instructor";
  seed: number | null;
  source: "playtomic" | "manual";
  playtomic_uid: string | null;
  team_id: number | null;
};

export type Team = {
  id: number;
  p1: number | null;
  p2: number | null;
  group_label: string | null;
  seed: number | null;
  origin: string;
  label: string;
  avg_level: number | null;
  complete: boolean;
};

export type Match = {
  id: number;
  phase: Phase;
  group_label: string | null;
  round_no: number | null;
  court: string | null;
  team_a: number | null;
  team_b: number | null;
  a1: number | null;
  a2: number | null;
  b1: number | null;
  b2: number | null;
  label_a: string | null;
  label_b: string | null;
  games_a: number | null;
  games_b: number | null;
  scoring: "time" | "set" | "set_time";
  status: "pending" | "done";
  locked: number;
  version: number;
  updated_by?: string | null;
  updated_at: string | null;
};

export type StandingRow = {
  team_id?: number;
  player_id?: number;
  label: string;
  rank: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  gf: number;
  ga: number;
  diff: number;
  points: number;
};

export type FinalRow = {
  position: number;
  team_id: number | null;
  label: string;
  players: string[];
  points: number;
};

export type GaraState = {
  gara: Omit<GaraSummary, "n_players" | "n_teams" | "n_matches" | "n_done"> & {
    config: GaraConfig;
    closed_at: string | null;
  };
  players: Player[];
  teams: Team[];
  matches: Match[];
  groups: Record<string, StandingRow[]>;
  individual: StandingRow[];
  final: FinalRow[];
  warnings: string[];
  progress: { done: number; total: number };
};

export type Upcoming = {
  tid: string;
  name: string;
  start_at: string;
  registered: number;
  max_players: number | null;
  level: string;
  imported_id: number | null;
};

export type PointsConfig = { presence: number; per_player_behind: number; podium: Record<string, number> };

export type RoadToEntry = {
  rank: number;
  name: string;
  points: number;
  played: number;
  wins: number;
  podiums: number;
  best: number | null;
  history: { gara_id: number; name: string; category: string; day: string; position: number; points: number; players: number }[];
};

export type RoadTo = {
  season: { name: string; start: string; end: string };
  points: PointsConfig;
  tournaments: number;
  ranking: RoadToEntry[];
};

export type StaffMember = { id: number; name: string; active: number; has_pin: number };
export type Actor = { name: string; admin: boolean };
export type LineupTeam = { p1: number | null; p2: number | null; group_label: string | null; origin?: string };

// ─────────────────────────────────────────────────────────── sessione staff
const TOKEN_KEY = "mp_gare_staff";

export type StaffSession = { token: string; name: string; expires_at: number };

export const staffSession = {
  get(): StaffSession | null {
    try {
      const s = JSON.parse(localStorage.getItem(TOKEN_KEY) || "null") as StaffSession | null;
      return s && s.expires_at * 1000 > Date.now() ? s : null;
    } catch {
      return null;
    }
  },
  set(s: StaffSession) {
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(s));
    } catch {
      /* storage non disponibile: la sessione dura finché la pagina resta aperta */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* niente */
    }
  },
};

export const isStaffApp = () => typeof window !== "undefined" && window.location.pathname.startsWith("/staff");

// ─────────────────────────────────────────────────────────── fetch
export class GareApiError extends Error {
  status: number;
  detail: Record<string, unknown>;
  constructor(status: number, message: string, detail: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function call<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth && isStaffApp()) {
    headers["X-Gare-Client"] = "staff";
    const s = staffSession.get();
    if (s) headers.Authorization = `Bearer ${s.token}`;
  }
  // limite di 15 s: con una rete lenta a bordo campo meglio fallire e mettere il
  // risultato in attesa che restare appesi
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 15_000);
  let res: Response;
  let text: string;
  try {
    res = await fetch(window.location.origin + "/api" + path, {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: ctrl.signal,
    });
    text = await res.text();
  } finally {
    clearTimeout(timer);
  }
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const det = (data && typeof data.detail === "object" ? data.detail : { message: data?.detail }) as Record<string, unknown>;
    const msg = String(det?.message || `Errore ${res.status}`);
    if (res.status === 401 && isStaffApp() && auth) {
      staffSession.clear();
      window.location.reload();
    }
    throw new GareApiError(res.status, msg, det || {});
  }
  return data as T;
}

/** Vero se la richiesta non è proprio arrivata al server (niente rete o troppo lenta). */
export const isNetworkError = (e: unknown) =>
  !(e instanceof GareApiError) && (e instanceof TypeError || (e instanceof Error && e.name === "AbortError"));

// ─────────────────────────────────────────────────────────── risultati in attesa
// Senza connessione il risultato resta sul telefono (anche chiudendo la pagina)
// e parte da solo appena torna la rete.
export type PendingResult = {
  gid: number;
  mid: number;
  games_a: number | null;
  games_b: number | null;
  version: number;
  ts: number;
};

const PENDING_KEY = "mp_gare_pending";
let pendingCache: PendingResult[] | null = null;
const pendingListeners = new Set<() => void>();

function readPending(): PendingResult[] {
  if (pendingCache) return pendingCache;
  try {
    pendingCache = JSON.parse(localStorage.getItem(PENDING_KEY) || "[]") as PendingResult[];
  } catch {
    pendingCache = [];
  }
  return pendingCache;
}

function writePending(list: PendingResult[]) {
  pendingCache = list;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch {
    /* storage non disponibile: resta in memoria finché la pagina è aperta */
  }
  pendingListeners.forEach((fn) => fn());
}

export const pending = {
  list: readPending,
  add(p: PendingResult) {
    writePending([...readPending().filter((x) => !(x.gid === p.gid && x.mid === p.mid)), p]);
  },
  remove(gid: number, mid: number) {
    writePending(readPending().filter((x) => !(x.gid === gid && x.mid === mid)));
  },
  subscribe(fn: () => void) {
    pendingListeners.add(fn);
    return () => {
      pendingListeners.delete(fn);
    };
  },
};

const g = (gid: number) => `/gare/${gid}`;

export const gare = {
  me: () => call<Actor>("GET", "/gare/me"),
  list: () => call<GaraSummary[]>("GET", "/gare"),
  upcoming: () => call<Upcoming[]>("GET", "/gare/playtomic/upcoming"),
  importPlaytomic: (tid: string) => call<GaraState>("POST", "/gare/import", { tid }),
  create: (b: { name: string; start_at?: string | null; category?: string; road_to?: boolean }) =>
    call<GaraState>("POST", "/gare", b),
  get: (gid: number) => call<GaraState>("GET", g(gid)),
  patch: (gid: number, b: Partial<{ name: string; category: string; start_at: string; road_to: boolean; format: GaraFormat; config: Partial<GaraConfig> }>) =>
    call<GaraState>("PATCH", g(gid), b),
  remove: (gid: number) => call<{ ok: boolean }>("DELETE", g(gid)),
  syncPlaytomic: (gid: number) =>
    call<GaraState & { sync: { added: number; removed: number } }>("POST", `${g(gid)}/sync-playtomic`),
  autoPair: (gid: number) => call<GaraState>("POST", `${g(gid)}/auto-pair`),
  lineup: (gid: number, teams: LineupTeam[]) => call<GaraState>("PUT", `${g(gid)}/lineup`, { teams }),
  order: (gid: number, player_ids: number[]) => call<GaraState>("PUT", `${g(gid)}/order`, { player_ids }),
  addPlayer: (gid: number, b: { name: string; level?: number | null }) => call<GaraState>("POST", `${g(gid)}/players`, b),
  patchPlayer: (gid: number, pid: number, b: Partial<{ name: string; level: number; role: Player["role"] }>) =>
    call<GaraState>("PATCH", `${g(gid)}/players/${pid}`, b),
  deletePlayer: (gid: number, pid: number) => call<GaraState>("DELETE", `${g(gid)}/players/${pid}`),
  start: (gid: number) => call<GaraState>("POST", `${g(gid)}/start`),
  reset: (gid: number, force = false) => call<GaraState>("POST", `${g(gid)}/reset`, { force }),
  result: (gid: number, mid: number, games_a: number | null, games_b: number | null, version: number) =>
    call<GaraState>("PUT", `${g(gid)}/matches/${mid}`, { games_a, games_b, version }),
  matchTeams: (gid: number, mid: number, b: { team_a?: number; team_b?: number; auto?: boolean }) =>
    call<GaraState>("PUT", `${g(gid)}/matches/${mid}/teams`, b),
  close: (gid: number) => call<GaraState>("POST", `${g(gid)}/close`),
  reopen: (gid: number) => call<GaraState>("POST", `${g(gid)}/reopen`),
  roadto: () => call<RoadTo>("GET", "/gare/roadto"),
  setPoints: (p: PointsConfig) => call<RoadTo>("PUT", "/gare/roadto/points", p),
  staff: () => call<StaffMember[]>("GET", "/gare/staff"),
  setStaff: (b: { name: string; pin?: string; active?: boolean }) => call<StaffMember[]>("PUT", "/gare/staff", b),
  // senza password
  staffNames: () => call<string[]>("GET", "/public/gare/staff-names", undefined, false),
  login: (name: string, pin: string) => call<StaffSession>("POST", "/public/gare/login", { name, pin }, false),
};

// ─────────────────────────────────────────────────────────── etichette
export const FORMAT_LABEL: Record<GaraFormat, string> = {
  gironi_tabellone: "Gironi + tabellone",
  coppie_fisse: "Coppie fisse (tutti contro tutti)",
  giro_a_8: "Giro a 8 (compagno a rotazione)",
};

export const PHASE_LABEL: Record<Phase, string> = {
  round: "Giro",
  group: "Girone",
  semi: "Semifinale Diamond",
  final: "Finale Diamond 1°-2°",
  third: "Finale Diamond 3°-4°",
  gold_semi: "Semifinale Gold",
  gold_final: "Finale Gold 5°-6°",
  gold_third: "Finale Gold 7°-8°",
};

export const STATUS_LABEL: Record<GaraStatus, string> = {
  setup: "Da preparare",
  live: "In corso",
  finished: "Concluso",
};

export function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
